<template>
    <header class="z-50">
        <nav class="bg-background-accent border-b border-background-accent-dark">
            <div class="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
                <div class="relative flex h-16 items-center justify-between">
                    <div v-if="navItems.length" class="absolute inset-y-0 left-0 flex items-center sm:hidden">
                        <!-- Mobile menu button-->
                        <button
                            type="button"
                            class="relative inline-flex items-center justify-center rounded-md p-2 text-text-light hover:bg-background-accent-dark hover:text-text focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                            aria-controls="mobile-menu"
                            aria-expanded="false"
                        >
                            <span @click="menuOpen = !menuOpen" class="absolute -inset-0.5"></span>
                            <span class="sr-only">Open main menu</span>
                            <!--
                              Icon when menu is closed.

                              Menu open: "hidden", Menu closed: "block"
                            -->
                            <svg
                                class="block h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                                />
                            </svg>
                            <!--
                              Icon when menu is open.

                              Menu open: "block", Menu closed: "hidden"
                            -->
                            <svg
                                class="hidden h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                    <div class="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                        <div class="flex shrink-0 items-center">
                            <NuxtLink :to="localePath('/dashboard')" class="bg-transparent!">
                                <img class="h-8 w-auto" :src="logo.src" :alt="logo.alt">
                            </NuxtLink>
                        </div>
                        <div v-if="navItems.length" class="hidden sm:ml-6 sm:block">
                            <div class="flex space-x-4">
                                <template v-for="item in navItems" :key="item.key">
                                    <template v-if="item.children && item.children.length <= (item.inlineUpTo ?? 0)">
                                        <NuxtLink
                                            v-for="child in item.children"
                                            :key="child.to"
                                            :to="localePath(child.to)"
                                            class="text-text hover:bg-background-accent-light rounded-md px-3 py-2 text-sm font-medium"
                                        >{{ child.label }}
                                        </NuxtLink>
                                    </template>
                                    <details
                                        v-else-if="item.children"
                                        ref="dropdownElements"
                                        class="group relative"
                                    >
                                        <summary
                                            class="flex cursor-pointer list-none items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-text hover:bg-background-accent-light [&::-webkit-details-marker]:hidden"
                                        >
                                            {{ item.label }}
                                            <svg
                                                class="h-4 w-4 transition-transform group-open:rotate-180"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                aria-hidden="true"
                                            >
                                                <path d="m5 7.5 5 5 5-5"/>
                                            </svg>
                                        </summary>
                                        <div
                                            class="absolute left-0 z-50 mt-2 flex max-h-96 min-w-48 flex-col overflow-y-auto overflow-x-hidden rounded-md border border-primary bg-background-accent shadow-lg"
                                        >
                                            <NuxtLink
                                                v-for="child in item.children"
                                                :key="child.to"
                                                :to="localePath(child.to)"
                                                class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-text transition-colors first:pt-3 last:pb-3 hover:bg-primary hover:text-background"
                                                @click="closeDropdowns"
                                            >
                                                <span v-if="child.emoji" aria-hidden="true">{{ child.emoji }}</span>
                                                {{ child.label }}
                                            </NuxtLink>
                                        </div>
                                    </details>
                                    <NuxtLink
                                        v-else-if="item.to"
                                        :to="localePath(item.to)"
                                        class="relative text-text hover:bg-background-accent-light rounded-md px-3 py-2 text-sm font-medium flex gap-2"
                                    >{{ item.label }}
                                        <ClientOnly v-if="item.badge !== undefined">
                                            <span
                                                v-show="(item.badge ?? 0) > 0"
                                                class="bg-error rounded-full text-background text-xs px-1.5 min-w-4 text-center leading-5"
                                            >{{ item.badge }}</span>
                                        </ClientOnly>
                                    </NuxtLink>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="userIsLoggedIn"
                        class="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0"
                    >
                        <!-- Profile dropdown -->
                        <div class="relative ml-3" @click.prevent="() => profileOpen = !profileOpen">
                            <div>
                                <button
                                    type="button"
                                    class="relative flex rounded-full bg-background-accent-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-accent"
                                    id="user-menu-button"
                                    aria-expanded="false"
                                    aria-haspopup="true"
                                >
                                    <span class="absolute -inset-1.5"></span>
                                    <span class="sr-only">Open user menu</span>
                                    <img
                                        v-if="userStore.avatar"
                                        class="h-8 w-8 rounded-full"
                                        :src="userStore.avatar"
                                        alt=""
                                    >
                                    <svg
                                        v-else
                                        class="h-8 w-8 rounded-full stroke-primary bg-background-accent border-2 border-primary"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 640 640"
                                    ><!--!Font Awesome Free v7.1.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2026 Fonticons, Inc.-->
                                        <path d="M240 192C240 147.8 275.8 112 320 112C364.2 112 400 147.8 400 192C400 236.2 364.2 272 320 272C275.8 272 240 236.2 240 192zM448 192C448 121.3 390.7 64 320 64C249.3 64 192 121.3 192 192C192 262.7 249.3 320 320 320C390.7 320 448 262.7 448 192zM144 544C144 473.3 201.3 416 272 416L368 416C438.7 416 496 473.3 496 544L496 552C496 565.3 506.7 576 520 576C533.3 576 544 565.3 544 552L544 544C544 446.8 465.2 368 368 368L272 368C174.8 368 96 446.8 96 544L96 552C96 565.3 106.7 576 120 576C133.3 576 144 565.3 144 552L144 544z"/>
                                    </svg>
                                </button>
                            </div>

                            <!--
                              Dropdown menu, show/hide based on menu state.

                              Entering: "transition ease-out duration-100"
                                From: "transform opacity-0 scale-95"
                                To: "transform opacity-100 scale-100"
                              Leaving: "transition ease-in duration-75"
                                From: "transform opacity-100 scale-100"
                                To: "transform opacity-0 scale-95"
                            -->
                            <Transition name="fadePop">
                                <div
                                    v-show="profileOpen"
                                    class="absolute border border-primary right-0 z-99 mt-2 w-48 origin-top-right rounded-md bg-background-accent pb-1 shadow-lg ring-opacity-5 focus:outline-none"
                                    role="menu"
                                    aria-orientation="vertical"
                                    aria-labelledby="user-menu-button"
                                    tabindex="-1"
                                    ref="profileDropdownElement"
                                >
                                    <!-- Active: "bg-gray-100", Not Active: "" -->
                                    <div class="px-4 py-2 bg-primary-light border-b border-primary rounded-t-md text-primary">{{ userStore.name }}</div>
                                    <NuxtLink :to="localePath('/profile')">
                                        <div
                                            class="block px-4 py-2 text-sm text-text hover:bg-primary hover:text-background transition-colors"
                                            role="menuitem"
                                            tabindex="-1"
                                            id="user-menu-item-0"
                                        >
                                            {{ t('yourProfile') }}
                                        </div>

                                    </NuxtLink>
                                    <NuxtLink :to="localePath('/profile/settings')">
                                        <div
                                            class="block px-4 py-2 text-sm text-text hover:bg-primary hover:text-background transition-colors"
                                            role="menuitem"
                                            tabindex="-1"
                                            id="user-menu-item-1"
                                        >
                                            {{ t('yourSettings') }}
                                        </div>
                                    </NuxtLink>
                                    <NuxtLink :to="localePath('/admin/settings')">
                                        <div
                                            v-if="userStore.$state.permissions.includes(UserPermission.admin)"
                                            class="block px-4 py-2 text-sm text-text hover:bg-primary hover:text-background transition-colors"
                                            role="menuitem"
                                            tabindex="-1"
                                            id="user-menu-item-1"
                                        >
                                            {{ t('adminSettings') }}
                                        </div>
                                    </NuxtLink>
                                    <div
                                        class="block px-4 py-2 text-sm text-text hover:bg-primary hover:text-background transition-colors"
                                        role="menuitem"
                                        tabindex="-1"
                                        id="user-menu-item-1"
                                        @click="logout"
                                    >
                                        {{ t('logout') }}
                                    </div>
                                </div>
                            </Transition>
                        </div>
                    </div>
                    <toaster/>
                </div>
            </div>

            <!-- Mobile menu, show/hide based on menu state. -->
            <div
                v-show="menuOpen"
                id="mobile-menu"
                class="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-y-contain sm:hidden"
            >
                <div class="space-y-1 px-2 pb-3 pt-2 flex flex-col">
                    <template v-for="item in navItems" :key="item.key">
                        <div
                            v-if="item.children"
                            class="flex flex-col gap-1"
                            :aria-labelledby="`mobile-${ item.key }-heading`"
                        >
                            <p
                                :id="`mobile-${ item.key }-heading`"
                                class="px-3 pt-2 text-xs font-bold uppercase tracking-wider text-text-light"
                            >
                                {{ item.label }}
                            </p>
                            <NuxtLink
                                v-for="child in item.children"
                                :key="child.to"
                                :to="localePath(child.to)"
                                class="ml-4 flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-text hover:bg-background-accent-light"
                            >
                                <span v-if="child.emoji" aria-hidden="true">{{ child.emoji }}</span>
                                {{ child.label }}
                            </NuxtLink>
                        </div>
                        <NuxtLink
                            v-else-if="item.to"
                            :to="localePath(item.to)"
                            class="relative text-text hover:bg-background-accent-light rounded-md px-3 py-2 text-sm font-medium flex gap-2 items-center"
                        >
                            {{ item.label }}
                            <ClientOnly v-if="item.badge !== undefined">
                                <span
                                    v-show="(item.badge ?? 0) > 0"
                                    class="bg-error rounded-full text-background text-xs px-1.5 min-w-4 text-center leading-5"
                                >{{ item.badge }}</span>
                            </ClientOnly>
                        </NuxtLink>
                    </template>
                </div>
            </div>
        </nav>
    </header>
</template>

<script lang="ts" setup>
import { useMediaQuery, useScrollLock } from '@vueuse/core'
import toaster from './toaster.vue'
// import Icon from 'vue-awesome/components/Icon';
// import 'vue-awesome/icons/arrow-left';

const localePath = useLocalePath()
const { t } = useI18n({ useScope: 'local' })

const profileOpen = ref(false)
const menuOpen = ref(false)
const isMobileViewport = useMediaQuery('(max-width: 639px)')
const bodyScrollLocked = useScrollLock(import.meta.client ? document.body : null)

const profileDropdownElement = ref<HTMLElement>()
const dropdownElements = ref<HTMLDetailsElement[]>([])

const mainStore = useMainStore()
const userStore = useUserStore()
const activityStore = useActivityStore()
const appConfig = useAppConfig()

const logo = computed(() => ({
    src: appConfig.cms?.header?.logo?.src ?? '/assets/images/rood-letter-193.png',
    alt: appConfig.cms?.header?.logo?.alt ?? 'Yipp',
}))

const userIsLoggedIn = computed(() => {
    return userStore.isLoggedIn()
})

type NavChild = { to: string, label: string, emoji?: string }
// A link (`to`) or a group (`children`); groups with at most `inlineUpTo` children render inline on desktop.
type NavItem = { key: string, label: string, to?: string, children?: NavChild[], inlineUpTo?: number, badge?: number }

const galleryLinks = computed<NavChild[]>(() => [
    { enabled: mainStore.header.galleries.image, label: t('imageGallery'), to: '/image-gallery' },
    { enabled: mainStore.header.galleries.video, label: t('videoGallery'), to: '/video-gallery' },
    { enabled: mainStore.header.galleries.file, label: t('filesGallery'), to: '/files-gallery' },
    { enabled: mainStore.header.galleries.audio, label: t('audioGallery'), to: '/audio-gallery' },
].filter(gallery => gallery.enabled).map(({ label, to }) => ({ label, to })))

const bucketLinks = computed<NavChild[]>(() => mainStore.buckets
    .filter(bucket => !bucket.private)
    .map(bucket => ({
        label: bucket.name,
        emoji: bucket.emoji,
        to: `/bucket/${bucket.slug}${bucket.singleton ? '/edit/latest' : ''}`,
    })))

// Rendered by both the desktop and the mobile menu.
const navItems = computed<NavItem[]>(() => {
    if (!userIsLoggedIn.value) return []
    const items: NavItem[] = [
        { key: 'dashboard', label: t('dashboard'), to: '/dashboard' },
        { key: 'galleries', label: t('galleries'), children: galleryLinks.value, inlineUpTo: 2 },
        { key: 'buckets', label: t('buckets'), children: bucketLinks.value },
    ]
    if (userStore.isAdmin()) items.push(
        { key: 'activity', label: t('activity'), to: '/activity', badge: activityStore.unseenCount },
        { key: 'bucketSettings', label: t('bucketSettings'), to: '/admin/bucket/overview' },
    )
    return items.filter(item => !item.children || item.children.length)
})

const closeDropdowns = () => {
    for (const dropdown of dropdownElements.value) dropdown.open = false
}

const route = useRoute()

watchEffect(() => {
    bodyScrollLocked.value = menuOpen.value && isMobileViewport.value
})

watch(() => route.fullPath, () => {
    menuOpen.value = false
})

const logout = async () => {
    await useAuth().logout()
    reloadNuxtApp()
}

onMounted(() => {
    if (userStore.isAdmin()) void activityStore.fetchUnseenCount().catch(error => {
        console.error('Could not fetch unseen activity count', error)
    })
    if (!process.server) {
        window.addEventListener('pointerup', (evt: PointerEvent) => {
            if (
                evt.target != profileDropdownElement.value &&
                !profileDropdownElement.value?.contains(evt.target as Node)
            ) {
                if (profileOpen.value) profileOpen.value = false
            }
            for (const dropdown of dropdownElements.value) {
                if (!dropdown.contains(evt.target as Node)) dropdown.open = false
            }
        })
    }
})

onBeforeUnmount(() => {
    bodyScrollLocked.value = false
})
</script>

<style scoped lang="scss">
header {
    width: 100vw;
}

.back-to-overview-container {
    line-height: 1;

    a {
        display: flex;
        align-items: center;

        .back-to-overview {
            margin-left: 0.333rem;
        }
    }
}

.fadePop-enter-active,
.fadePop-leave-active {
    transition: all 0.15s ease;
    // transition: transform 0.5s ease;
}

.fadePop-enter-from,
.fadePop-leave-to {
    opacity: 0;
    transform: translateX(50px)
}

.router-link-active {
    background-color: var(--color-slate-600);
    color: var(--color-indigo-100)
}
</style>


<i18n lang="yaml">
en:
    dashboard: Dashboard
    galleries: Galleries
    imageGallery: Image Gallery
    videoGallery: Video Gallery
    filesGallery: Files Gallery
    audioGallery: Audio Gallery
    buckets: Buckets
    activity: Activity
    bucketSettings: Bucket Settings
    yourSettings: Your settings
    yourProfile: Profile
    adminSettings: Admin settings
    logout: Log out
nl:
    dashboard: Overzicht
    galleries: Galerijen
    imageGallery: Afbeeldingengalerij
    videoGallery: Videogalerij
    filesGallery: Bestandengallerij
    audioGallery: Audiogallerij
    buckets: Kuipen
    activity: Activiteit
    bucketSettings: Kuip instellingen
    yourProfile: Profiel
    yourSettings: Jouw instellingen
    adminSettings: Administratie instellingen
    logout: Uitloggen
</i18n>
